// Auditoria de pedidos: registra tudo que acontece com cada pedido —
// criação, edição, exclusão, mudança de status. Fica gravado no próprio
// pedido em pedido.historico[] pra ser lido depois na aba "Histórico"
// do OrderDetail (só admin/gerente vê).
//
// Objetivo: gerente reclamou que vendedoras editam pedido calada e depois
// ninguém sabe se a versão que produziram é a original ou uma editada.
// Com o histórico, cada mudança fica registrada com quem fez, quando e
// o que mudou (antes → depois).

// Recupera o usuário logado atualmente. AuthContext grava sempre que
// o auth muda; savePedido lê daqui pra saber quem tá salvando.
export function getCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem('mini_current_user') || 'null')
  } catch { return null }
}

export function setCurrentUser(user) {
  if (user) localStorage.setItem('mini_current_user', JSON.stringify(user))
  else      localStorage.removeItem('mini_current_user')
}

// Campos que interessa auditar. Outros (id, historico, criadoEm) são
// metadados internos — mudança neles não é relevante pra o gerente.
const CAMPOS_AUDITAVEIS = [
  ['cliente.nome',      p => p.cliente?.nome],
  ['cliente.contato',   p => p.cliente?.contato],
  ['cliente.email',     p => p.cliente?.email],
  ['cliente.documento', p => p.cliente?.documento],
  ['cliente.endereco',  p => p.cliente?.endereco],
  ['cliente.cidade',    p => p.cliente?.cidade],
  ['cliente.bairro',    p => p.cliente?.bairro],
  ['numero',            p => p.numero],
  ['prazo',             p => p.prazo],
  ['dataVenda',         p => p.dataVenda],
  ['status',            p => p.status],
  ['valorTotal',        p => p.valorTotal],
  ['frete',             p => p.frete],
  ['vendedora',         p => p.vendedora],
  ['observacoesGerais', p => p.observacoesGerais],
  // Pagamento na entrega — rastro de quem/quando marcou pago.
  ['pagoEntrega',       p => !!p.pagoEntrega],
  // Móveis: apenas a quantidade (mudanças granulares poluiriam demais)
  ['moveis.qtd',        p => Array.isArray(p.moveis) ? p.moveis.length : 0],
]

// Compara 2 versões de pedido e retorna o array de mudanças detectadas.
// Vazio = nada mudou (não gera entrada de histórico).
function calcularMudancas(antes, depois) {
  const mudancas = []
  for (const [campo, get] of CAMPOS_AUDITAVEIS) {
    const va = get(antes)
    const vd = get(depois)
    // Compara como string pra evitar false positive de "" vs undefined
    if (String(va ?? '') !== String(vd ?? '')) {
      mudancas.push({ campo, antes: va, depois: vd })
    }
  }
  return mudancas
}

// Retorna nova entrada de histórico. Não muta nada.
function novaEntrada(tipo, mudancas, extra = {}) {
  const user = getCurrentUser()
  return {
    id:        crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    autor:    user?.username || 'desconhecido',
    autorRole: user?.role    || null,
    tipo,    // 'criacao' | 'edicao' | 'exclusao' | 'restauracao' | 'status'
    mudancas: mudancas || [],
    ...extra,
  }
}

// Cria uma nova entrada de histórico e retorna o pedido com ela
// concatenada em pedido.historico[]. Se não houver mudanças relevantes,
// retorna o pedido inalterado (evita entradas vazias).
export function registrarEdicao(pedidoAntes, pedidoDepois) {
  const mudancas = calcularMudancas(pedidoAntes, pedidoDepois)
  if (mudancas.length === 0) return pedidoDepois
  const entrada = novaEntrada('edicao', mudancas)
  const historico = [...(pedidoDepois.historico || []), entrada]
  return { ...pedidoDepois, historico }
}

// Registra criação (não há "antes" — só a versão inicial)
export function registrarCriacao(pedido) {
  const entrada = novaEntrada('criacao', [])
  const historico = [...(pedido.historico || []), entrada]
  return { ...pedido, historico }
}

// Registra que o pedido foi enviado pra lixeira (soft-delete)
export function registrarExclusao(pedido, motivo) {
  const entrada = novaEntrada('exclusao', [], { motivo: motivo || '' })
  const historico = [...(pedido.historico || []), entrada]
  return { ...pedido, historico }
}

// Registra restauração da lixeira
export function registrarRestauracao(pedido) {
  const entrada = novaEntrada('restauracao', [])
  const historico = [...(pedido.historico || []), entrada]
  return { ...pedido, historico }
}
